<div class="fullcontainer pr-3">
  <div class='sheet'>
    <div class='titleContainer'>
      <h1 class='header'>Report - Stock Reconciliation</h1>
      <div>
        <button class='btn btn-secondary' routerLink='/warehousing/reports'>All Reports</button>
      </div>
    </div>

    <p class='text-muted'>
      Each stored stock level set against the level recomputed as the sum of signed
      quantities over the ledger. The level is a cache of the ledger, not an independent
      number, so <strong>any variance is a defect</strong> rather than something to explain away.
    </p>

    <div class='row'>
      <div class='col-md-4'>
        <label>Warehouse</label>
        <select class='form-control' (change)='this.onWarehouseChanged($event.target.value)'>
          <option value='-1'>All warehouses</option>
          @for (item of this.warehouses; track item.id) {
            <option [value]='item.id'>{{ item.name }}</option>
          }
        </select>
      </div>
      <div class='col-md-8'>
        <label>&nbsp;</label>
        <p class='form-text text-muted mt-2 mb-0'>
          The ledger is replayed server-side and only pairs that disagree come back, with the
          float tolerance already applied. So every row below is a real defect rather than
          rounding noise — there is nothing to filter.
        </p>
      </div>
    </div>
    <br />
    <br />
    <button class='btn btn-primary' (click)='this.loadReport()' [disabled]='this.running'>Run Report</button>
    <hr />
    <button class='btn btn-secondary' (click)='exportToCSV()' [disabled]='this.visibleRows.length === 0'>Export to CSV</button>
  </div>

  <div class='sheet'>
    @if (this.running) {
      <p class='m-0'><i class="fas fa-spinner fa-spin"></i> Recomputing from the ledger&hellip;</p>
    }

    <!-- An empty table after a failure would read as "everything reconciles", which is the
         worst possible way for this screen to be wrong. So a failure says so loudly. -->
    @if (this.loadFailed) {
      <div class='alert alert-danger mb-0'>
        <strong>The reconciliation could not be run.</strong>
        {{ this.loadError }}
        This is <em>not</em> a clean result — nothing has been checked.
        <button class='btn btn-sm btn-danger ml-2' (click)='this.loadReport()'>Try again</button>
      </div>
    }

    @if (this.hasRun && !this.running && !this.loadFailed) {
      <div class='titleContainer'>
        <h1 class='header'>Results</h1>
      </div>

      @if (this.balanced && this.varianceCount === 0) {
        <div class='alert alert-success'>
          <strong>Every level matches the ledger.</strong>
          {{ this.checked }} product and location pair(s) checked.
        </div>
      }

      @if (this.varianceCount > 0) {
        <div class='alert alert-danger'>
          <strong>{{ this.varianceCount }} of {{ this.checked }} pair(s) do not reconcile.</strong>
          A stock level that disagrees with the ledger behind it means movements have been
          lost, double-counted or mis-signed. The ledger is the record of what happened, so
          it is the level that is wrong.
        </div>
      }

      @if (this.visibleRows.length > 0) {
        <table class='table'>
          <thead>
            <th>Product</th>
            <th>SKU</th>
            <th>Warehouse</th>
            <th>Aisle</th>
            <th>Shelf</th>
            <th class='text-right'>Level Qty</th>
            <th class='text-right'>Ledger Qty</th>
            <th class='text-right'>Variance</th>
            <th class='text-right'>Movements</th>
            <th>Issue</th>
            <th></th>
          </thead>
          <tbody>
            @for (row of this.visibleRows; track row.productId + '-' + row.warehouseLocationId) {
              <tr class='table-danger'>
                <td>{{ row.productName }}</td>
                <td>{{ row.sku }}</td>
                <td>{{ row.warehouseName }}</td>
                <td>{{ row.aisle }}</td>
                <td>{{ row.shelf }}</td>
                <!-- A missing side renders as a dash, never as 0 — a level of genuinely
                     zero and no level row at all are different problems. -->
                <td class='text-right' [class.text-muted]='this.isMissingLevel(row)'>
                  {{ this.formatQty(row.levelQty) }}
                </td>
                <td class='text-right' [class.text-muted]='this.isMissingLedger(row)'>
                  {{ this.formatQty(row.ledgerQty) }}
                </td>
                <td class='text-right'>
                  <strong class='text-danger'>{{ this.formatQty(row.difference) }}</strong>
                </td>
                <td class='text-right'>{{ row.movements }}</td>
                <td>{{ this.issueOf(row) }}</td>
                <td>
                  <button class='btn btn-warning'
                    routerLink='/warehousing/reports/movement-history'
                    [queryParams]='{productId: row.productId}'>Audit trail</button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      }
    }
  </div>
</div>
